import styles from "./AutodocSection.module.scss";
import { Note } from "./Note";
import { SectionCard } from "./SectionCard";

export function AutodocSection() {
  return (
    <SectionCard
      id="autodoc"
      ariaLabelledby="autodoc-h"
      title="Autodoc — Automated RFP Responses and Bated Production Packages"
      description="Autodoc searches your case documents, finds what's responsive to each RFP, applies Bates numbers, and drafts substantive responses with citations."
    >
      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>★</span> Before You Begin
        </h3>
        <ul>
          <li>
            <strong>PDFs only</strong> in Version 1 (complaint + case docs).
          </li>
          <li>
            Check for <strong>privilege</strong> before upload (V1 has no
            auto-privilege check/redaction).
          </li>
          <li>
            Best for productions up to ~<strong>10,000</strong> pages (not a
            full e-discovery replacement).
          </li>
        </ul>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>1</span> Upload Opposing Counsel's
          Request for Production
        </h3>
        <ol>
          <li>
            Click <strong>Upload Document</strong> and upload the{" "}
            <em>RFP (PDF)</em>.
          </li>
          <li>Confirm the case info as usual.</li>
          <li>
            On your first response, locate the new button:{" "}
            <strong>Find Documents</strong>.
          </li>
        </ol>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>2</span> Set Up Your Production Page
        </h3>
        <ol>
          <li>
            Click <strong>Find Documents</strong> → you'll land on the case's{" "}
            <strong>Production</strong> page.
          </li>
          <li>
            Upload:
            <ul>
              <li>
                The <strong>Complaint</strong> (combine counterclaims into the
                uploaded .pdf if applicable).
              </li>
              <li>
                <strong>All case documents</strong> you might produce (records,
                emails, reports, contracts, etc.).
              </li>
            </ul>
          </li>
          <li>
            Click <strong>Process</strong>. Processing can take hours — you'll
            get an <strong>email</strong> when ready.
          </li>
        </ol>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>3</span> Processing Complete - Start
          Autodoc'ing
        </h3>
        <ol>
          <li>
            Once you receive the email letting you know the processing is
            complete, return to your RFP response, go to the first response, and
            click <strong>Find Documents</strong> again.
          </li>
          <li>
            Autodoc reads the request, definitions, and your case summary, then
            searches the production set for responsive documents.
          </li>
        </ol>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>4</span> Don't Trust AI - Verify!
        </h3>
        <ol>
          <li>
            Once Autodoc finds your responsive documents, select "Verify".
          </li>
          <li>Then, select "View All".</li>
          <li>
            You will be brought to a folder structure that Briefpoint created to
            organize the documents you uploaded.
          </li>
          <li>
            If you uploaded a single, bulk PDF, Autodoc will{" "}
            <strong>split and title</strong> it into individual documents.
          </li>
          <li>
            How to Verify:
            <ul>
              <li>
                <strong>Dark Green folders</strong> = reviewed by Autodoc,
              </li>
              <li>
                <strong>Dark Green folders with "!" in them</strong> = folders
                that are highly responsive but contain non-produced documents.{" "}
                <strong>Always review.</strong>
              </li>
              <li>
                <strong>White folders</strong> = not reviewed. Check white
                folders if they appear relevant.
              </li>
            </ul>
          </li>
          <li>
            Add missed responsive docs, exclude privileged material, and confirm
            inclusions by selecting "Confirm and Add" on the bottom right of the
            screen.
          </li>
        </ol>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>5</span> Handle Special Scenarios
        </h3>
        <ul>
          <li>
            <strong>No responsive docs?</strong> Click{" "}
            <em>Unable to Produce</em> if you can't produce the documents or
            "Send to Client" to get your client's input.
          </li>
          <li>
            <strong>Privileged doc spotted?</strong> Mark as{" "}
            <em>Designate as Privileged</em> to exclude it from the production.
            All documents marked as privileged will be included in the
            "Withheld" tab of your case's production tab.
          </li>
        </ul>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>6</span> Apply Bates Numbers
        </h3>
        <ol>
          <li>
            Once finished, click <strong>Review</strong> and set your preferred
            Bates prefix and starting number.
          </li>
          <li>
            When you click "Download production", all responsive documents will
            be downloaded as a .zip file with their bates applied.
          </li>
        </ol>
        <Note>
          In Version 1, some margin-less PDFs (e.g., medical records) may
          overlap. Adjust in Adobe if needed.
        </Note>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>7</span> Important: Final Safety Check
        </h3>
        <ul>
          <li>
            <strong>Always</strong> review <strong>"Not Produced"</strong> to
            view{" "}
            <em>
              all documents you uploaded that were NOT included in the
              production.
            </em>
          </li>
          <li>
            Use this as a checklist to ensure nothing responsive is missed.
          </li>
          <li>
            Check the boxes for documents that should be produced, then select
            "Add to Response" (top right of the box) to manually add them into
            your responses.
          </li>
          <li>
            Before clicking "Open in Word" and "Download Production", there
            should be NO documents you want to produce listed in the "Not
            Produced" page.
          </li>
        </ul>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>8</span> Export Your Package
        </h3>
        <ol>
          <li>
            Click <strong>Download Production</strong> for a ZIP with
            Bates-stamped .pdfs.
          </li>
          <li>
            Click <strong>Open in Word</strong> for substantive responses citing
            Bates numbers.
          </li>
        </ol>
      </div>

      <div className={styles.stepCard}>
        <h3>
          <span className={styles.num}>⚑</span> Security & Limits
        </h3>
        <ul>
          <li>
            <strong>SOC 2 certified</strong>; penetration tested; data fully{" "}
            <strong>encrypted</strong>.
          </li>
          <li>
            <strong>PDF only</strong> in Version 1; more formats to come.
          </li>
          <li>
            Best for productions up to ~<strong>10,000</strong> docs/pages.
          </li>
        </ul>
      </div>

      <div className={styles.checklist}>
        <h3>Quick Checklist</h3>
        <ul>
          <li>Upload RFP (PDF).</li>
          <li>
            Upload complaint + case docs → <strong>Process</strong>.
          </li>
          <li>
            Open RFP → <strong>Find Documents</strong>.
          </li>
          <li>Verify coverage (green vs white folders).</li>
          <li>Exclude privilege → Apply Bates → Final check.</li>
          <li>
            <strong>Download Production</strong> + <strong>Open in Word</strong>
            .
          </li>
        </ul>
      </div>
    </SectionCard>
  );
}
